/**
 * Session Provider for Sanctuary
 *
 * Wraps the application with session context from Better Auth.
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useSession, type SessionStatus, type Session } from '@/lib/auth'

interface SessionContextValue {
  session: Session | null
  status: SessionStatus
  isAuthenticated: boolean
}

const SessionContext = createContext<SessionContextValue>({
  session: null,
  status: 'loading',
  isAuthenticated: false,
})

export function useSessionContext() {
  return useContext(SessionContext)
}

interface SessionProviderProps {
  children: React.ReactNode
}

export function SessionProvider({ children }: SessionProviderProps) {
  const { data: session, isPending, error } = useSession()
  const [status, setStatus] = useState<SessionStatus>('loading')

  useEffect(() => {
    if (isPending) {
      setStatus('loading')
    } else if (error || !session) {
      setStatus('unauthenticated')
    } else {
      setStatus('authenticated')
    }
  }, [session, isPending, error])

  const value: SessionContextValue = {
    session: session as Session | null,
    status,
    isAuthenticated: status === 'authenticated' && !!session,
  }

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  )
}
